import type React from 'react';
import { useCallback, useRef, useState } from 'react';

import { isBwWelfareImageId } from '@/utils/welfareImageHelpers';

import ViewerFilmstrip from './components/Filmstrip/ViewerFilmstrip';
import ImageMetaInfo from './components/ImageMetaInfo/ImageMetaInfo';
import ImageNavButtons from './components/ImageNavButtons/ImageNavButtons';
import ImageStage from './components/ImageStage/ImageStage';
import ViewerToolbar from './components/ImageToolbar/ViewerToolbar';
import { useImageSelection } from './hooks/useImageSelection';
import { useImageSwipe } from './hooks/useImageSwipe';
import { useViewerFullscreen } from './hooks/useViewerFullscreen';
import { useViewerKeyboard } from './hooks/useViewerKeyboard';
import type { ImageFeedback, ImageViewerOptions, ImageViewerProps } from './types';
import { variantBehaviour } from './variants';

import styles from './ImageViewer.module.scss';

const DEFAULT_OPTIONS = {
    boxes: 'hidden',
    magnifierEnabled: true,
    showHelp: true,
    liceRegion: 'norway',
} as const satisfies Required<ImageViewerOptions>;

export default function ImageViewer(props: ImageViewerProps) {
    const {
        variant,
        images = [],
        isOptoScaleAdmin,
        isColor = true,
        initialImageIndex = 0,
        daysWithImages = [],
        onDayChange = () => {},
        onCommitImageFeedback = () => {},
        options,
        isFetching = false,
    } = props;

    const resolved = { ...DEFAULT_OPTIONS, ...options };
    const behaviour = variantBehaviour(variant);

    const wrapperRef = useRef<HTMLDivElement>(null);
    const viewerRef = useRef<HTMLDivElement>(null);

    const [showBoxes, setShowBoxes] = useState<boolean>(resolved.boxes === 'on');
    const [magnifierOn, setMagnifierOn] = useState<boolean>(false);
    const [isZoomed, setIsZoomed] = useState<boolean>(false);

    const selection = useImageSelection(images, initialImageIndex);
    const imageIndex = selection.index;

    const { isFullScreen, toggleFullScreen } = useViewerFullscreen(wrapperRef);

    useViewerKeyboard({
        onStep: selection.step,
        onMagnifierHeld: setMagnifierOn,
        magnifierShortcutEnabled: resolved.magnifierEnabled,
    });

    const swipeHandlers = useImageSwipe(selection.step, !isZoomed);

    const handleFilmstripCardClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        selection.selectById(event.currentTarget.value);
    };

    /* The magnifier's CSS clamps its own width against the overlay's size, so the overlay
       reports its rect and we publish it here as custom properties.

       Set on the viewer only. Both consumers - .magnifyerLayer and .magnifyer - are
       descendants of it and custom properties inherit, so the previous version's second
       write onto the magnifier layer was redundant. It was also unreliable: the overlay
       measures itself when it mounts, while the magnifier layer only exists once the
       magnifier is switched on, so that write usually landed on null. */
    const handleOverlayMeasured = useCallback((rect: DOMRect) => {
        viewerRef.current?.style.setProperty('--mlayer-width', `${rect.width}px`);
        viewerRef.current?.style.setProperty('--mlayer-height', `${rect.height}px`);
    }, []);

    const handleMagnifierPointerMove = (event: React.MouseEvent<HTMLDivElement>) => {
        const { height, width } = event.currentTarget.getBoundingClientRect();
        const { offsetX, offsetY } = event.nativeEvent;

        wrapperRef.current?.style.setProperty('--transform-x', '-100%');
        viewerRef.current?.style.setProperty('--mouse-x', `${offsetX}`);
        viewerRef.current?.style.setProperty('--mouse-y', `${offsetY}`);
        viewerRef.current?.style.setProperty('--percentage-left', `${(offsetX / width) * 100}`);
        viewerRef.current?.style.setProperty('--percentage-top', `${(offsetY / height) * 100}`);
    };

    const currentVisibleImage = selection.image;

    /* Merged welfare rows carry both colour and B&W frames under one id prefix, so the
       image itself can override the caller's `isColor`. */
    const effectiveIsColor =
        currentVisibleImage && isBwWelfareImageId(currentVisibleImage.id) ? false : isColor;

    const handleShareFalseDetectionFeedback = (feedbackDto: ImageFeedback) => {
        // Copy rather than assigning onto the caller's object, and snapshot the image so
        // the DTO does not carry a live reference into the React Query cache entry.
        onCommitImageFeedback({
            ...feedbackDto,
            imageMetadataDto: currentVisibleImage ? { ...currentVisibleImage } : undefined,
        });
    };

    return (
        <div
            ref={wrapperRef}
            className={styles.imageViewer}
            data-arrow-nav="images"
            data-fetching={isFetching ? 'true' : undefined}
            aria-busy={isFetching}>
            <div className={styles.controllbar}>
                <ImageMetaInfo
                    time_stamp={
                        currentVisibleImage?.localTimeStamp ?? currentVisibleImage?.timeStamp
                    }
                    formatted_time_stamp={currentVisibleImage?.formattedTimeStamp}
                    numberOfImages={images.length}
                    index={imageIndex}
                    hasImages={images.length > 0}
                />
                <ViewerToolbar
                    images={images}
                    imageIndex={imageIndex}
                    currentImage={currentVisibleImage}
                    behaviour={behaviour}
                    options={resolved}
                    isOptoScaleAdmin={isOptoScaleAdmin}
                    isColor={effectiveIsColor}
                    showBoxes={showBoxes}
                    onShowBoxesChange={setShowBoxes}
                    magnifierOn={magnifierOn}
                    onMagnifierChange={setMagnifierOn}
                    isFullScreen={isFullScreen}
                    onToggleFullScreen={toggleFullScreen}
                    onCommitFeedback={handleShareFalseDetectionFeedback}
                />
            </div>
            <div ref={viewerRef}>
                <ImageNavButtons
                    onPrevious={() => selection.step('previous')}
                    onNext={() => selection.step('next')}
                    nextDisabled={selection.isLast}
                    previousDisabled={selection.isFirst}>
                    {/* Not a <main>: the page already has one, and a landmark inside it would
                        be a second. */}
                    <div className={styles.stage}>
                        {magnifierOn && (
                            <div
                                className={styles.magnifyerLayer}
                                onMouseMove={handleMagnifierPointerMove}
                            />
                        )}

                        {/* The swipe listens on the image's own ancestor rather than on a layer
                            above it: that layer used to swallow every wheel, pinch and drag
                            before the zoom could see them. */}
                        <section className={styles.swipeWindow} {...swipeHandlers}>
                            {currentVisibleImage && (
                                <ImageStage
                                    id={currentVisibleImage.id}
                                    path={currentVisibleImage.path}
                                    lazyLoad={variant !== 'bioscope'}
                                    timeStamp={
                                        currentVisibleImage.localTimeStamp ??
                                        currentVisibleImage.timeStamp
                                    }
                                    formattedTimeStamp={currentVisibleImage.formattedTimeStamp}
                                    dimensions={{
                                        width: currentVisibleImage.width,
                                        height: currentVisibleImage.height,
                                    }}
                                    bboxes={currentVisibleImage.bboxes}
                                    isColor={effectiveIsColor}
                                    magnifierOn={magnifierOn}
                                    showPoiMeta={showBoxes}
                                    onOverlayMeasured={handleOverlayMeasured}
                                    boxTooltipStyle={behaviour.boxTooltip}
                                    isOptoScaleAdmin={isOptoScaleAdmin}
                                    onZoomedChange={setIsZoomed}
                                />
                            )}
                        </section>
                    </div>
                </ImageNavButtons>
            </div>
            <div className={styles.overLayFooter}>
                <ViewerFilmstrip
                    images={images}
                    selectedIndex={imageIndex}
                    currentImageId={currentVisibleImage?.id}
                    currentDay={currentVisibleImage?.day}
                    daysWithImages={daysWithImages}
                    behaviour={behaviour}
                    liceRegion={resolved.liceRegion}
                    onDayChange={onDayChange}
                    onCardClick={handleFilmstripCardClick}
                />
            </div>
        </div>
    );
}
